// One operation per browser tab. Never store credentials, lookup history or the
// free-text audit reason here. A reload must not replace an ambiguous write's key.
export const MANUAL_GRANT_STORAGE_KEY = "cheemspay.admin.manual-grant.v1";

export interface StoredGrantRequest {
  username: string;
  appId: "bomana";
  productCode: string;
  durationDays: number;
  manualRef: string;
}

export interface StoredGrantReceipt {
  username: string;
  durationDays: number;
  resultingExpiry: string;
  idempotent: boolean;
}

export interface StoredManualGrant {
  request: StoredGrantRequest;
  receipt?: StoredGrantReceipt;
}

export function readStoredManualGrant(): StoredManualGrant | null {
  const raw = sessionStorage.getItem(MANUAL_GRANT_STORAGE_KEY);
  if (raw === null) return null;
  const saved = JSON.parse(raw) as StoredManualGrant;
  const request = saved?.request;
  if (!request || typeof request.username !== "string" || !/^[a-z0-9._]{3,30}$/.test(request.username)
    || request.appId !== "bomana" || typeof request.productCode !== "string" || !/^[a-z0-9._-]{1,80}$/.test(request.productCode)
    || !Number.isSafeInteger(request.durationDays) || request.durationDays < 1 || request.durationDays > 7300
    || typeof request.manualRef !== "string" || !/^[a-zA-Z0-9._:-]{1,120}$/.test(request.manualRef)) {
    throw new Error("保存的人工授权凭据无法识别，请先通过发放记录核对上次操作");
  }
  if (saved.receipt && (saved.receipt.username !== request.username || saved.receipt.durationDays !== request.durationDays
    || typeof saved.receipt.resultingExpiry !== "string" || !Number.isFinite(Date.parse(saved.receipt.resultingExpiry))
    || typeof saved.receipt.idempotent !== "boolean")) {
    throw new Error("保存的人工授权结果无法识别，请先通过发放记录核对上次操作");
  }
  return saved;
}

function saveManualGrant(request: StoredGrantRequest, receipt?: StoredGrantReceipt) {
  // Explicit allow-list prevents API responses or free-text fields leaking into storage.
  const saved: StoredManualGrant = { request: {
    username: request.username, appId: request.appId, productCode: request.productCode,
    durationDays: request.durationDays, manualRef: request.manualRef,
  } };
  if (receipt) saved.receipt = { username: receipt.username, durationDays: receipt.durationDays,
    resultingExpiry: receipt.resultingExpiry, idempotent: receipt.idempotent };
  sessionStorage.setItem(MANUAL_GRANT_STORAGE_KEY, JSON.stringify(saved));
}

function matchesRequest(current: StoredGrantRequest | undefined, request: StoredGrantRequest) {
  return current?.manualRef === request.manualRef && current.username === request.username
    && current.appId === request.appId && current.productCode === request.productCode
    && current.durationDays === request.durationDays;
}

export function prepareStoredManualGrant(request: StoredGrantRequest, recovering: boolean) {
  const current = readStoredManualGrant();
  if (!current) {
    if (recovering) throw new Error("原授权凭据已发生变化，请刷新后重新核对");
    saveManualGrant(request);
    return undefined;
  }
  if (!matchesRequest(current.request, request)) throw new Error("已有另一条待核对的授权操作，请刷新后先恢复该操作");
  // A successful old response may have arrived after this form read its recovery
  // snapshot. Preserve that receipt and avoid a needless same-key POST entirely.
  return current.receipt;
}

function isCurrentRequest(request: StoredGrantRequest) {
  return matchesRequest(readStoredManualGrant()?.request, request);
}

// A late response from an unmounted form must never replace or clear a newer
// operation's protection. These synchronous read/compare/write steps also avoid
// recreating a receipt after the administrator explicitly started the next grant.
export function completeStoredManualGrant(request: StoredGrantRequest, receipt: StoredGrantReceipt) {
  if (!isCurrentRequest(request)) return false;
  saveManualGrant(request, receipt);
  return true;
}

export function clearStoredManualGrant(request: StoredGrantRequest, allowCompleted = false) {
  if (!isCurrentRequest(request) || (!allowCompleted && readStoredManualGrant()?.receipt)) return false;
  sessionStorage.removeItem(MANUAL_GRANT_STORAGE_KEY);
  return true;
}
